import React from "react";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";

export interface AddButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	/** Button label, e.g. "Lägg till tagg". */
	label: React.ReactNode;
	/** Size of the leading plus icon in px. Defaults to 15. */
	iconSize?: number;
}

/**
 * Dashed-border "add / create" action button with a leading plus icon.
 *
 * The canonical way to offer an inline "add a new X" affordance in the new
 * Tailwind UI (e.g. "Lägg till tagg", "Skapa ny koncern"). Domain-free —
 * pass the label and an onClick. Restyle via `className` (merged with `cn`).
 */
const AddButton = React.forwardRef<HTMLButtonElement, AddButtonProps>(
	({ label, iconSize = 15, className, type = "button", ...props }, ref) => (
		<button
			ref={ref}
			type={type}
			className={cn(
				"inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-3.5 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:border-gray-400 hover:bg-gray-50 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-50",
				className
			)}
			{...props}
		>
			<Plus size={iconSize} />
			{label}
		</button>
	)
);

AddButton.displayName = "AddButton";

export default AddButton;
